/**
 * Die Navigation des Hauptlayouts (REQ-20 §3) — dunkle Spalte mit Kopf,
 * Dashboard-Eintrag, den Kategorien des Benutzers und einem festen Fuß.
 *
 * Sie ist seit dem Neuentwurf **kein** `position: fixed`-Element mehr, sondern
 * eine Flex-Spalte im Seitengerüst (REQ-20 §7). Ihre Breite kommt
 * ausschließlich aus `--navw`; das Ein- und Ausklappen schaltet `data-nav` am
 * `<html>`-Element um (`useNavMini`).
 *
 * Die Einträge stammen aus `useMyCategories()` und sind damit dynamisch — der
 * Entwurf verdrahtet sie fest (REQ-20 §2.1). Die dort gezeigten Zähler
 * (`2449`, `118`, …) entfallen: Jeder einzelne käme nur über einen eigenen
 * `maxcount`-Aufruf zustande, also über so viele Zusatzabfragen wie es Einträge
 * gibt (REQ-20 §3.2).
 */

import type { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useNavGroups } from '@/hooks/useNavGroups';
import { useNavMini } from '@/hooks/useNavMini';
import type { ChinaAppSysGroupCategory } from '@/types/entities';

import { NavGroup } from './NavGroup';
import { NavItem } from './NavItem';
import { adminNavIcon, collapseNavIcon, dashboardNavIcon } from './navIcons';

export interface LayoutSidebarProps {
  categories: ChinaAppSysGroupCategory[];
  onToggleAdmin: () => void;
}

export function LayoutSidebar({ categories, onToggleAdmin }: LayoutSidebarProps): ReactElement {
  const { currentApp } = useAuth();
  const { mini, setMini } = useNavMini();
  const { isExpanded, toggle } = useNavGroups();
  const { pathname } = useLocation();

  return (
    <nav className="iwm-nav" aria-label="Hauptnavigation">
      <div className="iwm-nav-head">
        <div className="iwm-nav-logo" aria-hidden="true">
          CF
        </div>
        <div className="iwm-nav-brand">
          {/* Produktname; darunter steht der Mandant aus der API. */}
          <div className="iwm-nav-brand-title">Cargoflow</div>
          <div className="iwm-nav-brand-app">{currentApp?.Name}</div>
        </div>
      </div>

      <div className="iwm-nav-scroll">
        <div className="iwm-nav-top">
          <NavItem
            icon={dashboardNavIcon()}
            label="Dashboard"
            to="/"
            active={pathname === '/'}
            className="iwm-nav-item-dashboard"
          />
        </div>

        {/* Kategorien ohne Ansichten erscheinen gar nicht — weder Gruppentitel
            noch leerer Einträgebereich (Ergänzung des Auftraggebers zu §3.1). */}
        {categories
          .filter((category) => (category.Views?.length ?? 0) > 0)
          .map((category) => (
            <NavGroup
              key={category.Id}
              category={category}
              // Im Mini-Modus sind die Gruppentitel ausgeblendet; gälten die
              // Gruppen dort nicht als aufgeklappt, wären ihre Einträge
              // unerreichbar (REQ-20 §3.1).
              expanded={mini || isExpanded(category.Id)}
              onToggle={() => {
                toggle(category.Id);
              }}
              currentPath={pathname}
            />
          ))}

        <div className="iwm-nav-scroll-end" />
      </div>

      <div className="iwm-nav-foot">
        <NavItem icon={adminNavIcon()} label="Administration" onClick={onToggleAdmin} />
        <NavItem
          icon={collapseNavIcon()}
          label="Einklappen"
          title={mini ? 'Sidebar ausklappen' : 'Einklappen'}
          className="iwm-nav-collapse"
          onClick={() => {
            setMini(!mini);
          }}
        />
      </div>
    </nav>
  );
}

export default LayoutSidebar;
